import { usePage } from '@inertiajs/vue3'
import { computed } from 'vue'

/**
 * Prefix an internal path with the active language code when it differs from
 * the website's default. Pure helper — exported so it's unit-testable.
 *
 * Pass-through rules (path unchanged):
 *  - lang is missing (admin / non-public pages set no lang prop)
 *  - lang equals defaultLang (canonical un-prefixed form)
 *  - path is not a string or doesn't start with '/'
 *  - path looks like a protocol-relative or absolute URL ("//", "http", "mailto:", "tel:", "#")
 *  - path is already prefixed with /{someKnownLang}/ — caller intent honoured
 */
export function withLangPrefix(path, lang, defaultLang) {
  if (typeof path !== 'string' || path.length === 0) {
    return path
  }
  if (!lang || !defaultLang || lang === defaultLang) {
    return path
  }
  if (!path.startsWith('/') || path.startsWith('//')) {
    return path
  }

  const langPrefix = `/${lang}`
  if (path === langPrefix || path.startsWith(`${langPrefix}/`)) {
    return path
  }

  return path === '/' ? langPrefix : `${langPrefix}${path}`
}

/**
 * Composable returning a reactive helper that prefixes the given path with the
 * currently active language when needed. Reads lang + default lang from the
 * Inertia page props so it stays in sync with navigation.
 *
 * @example
 *   const langPath = useLangPath()
 *   <a :href="langPath('/news')">News</a>
 */
export function useLangPath() {
  const page = usePage()

  return (path) => withSitePrefix(path, page.props)
}

/**
 * Same as useLangPath() but returns a computed wrapping a given path. Useful
 * when the path itself is reactive.
 */
export function useLangPathFor(pathRef) {
  const page = usePage()

  return computed(() => withSitePrefix(typeof pathRef === 'function' ? pathRef() : (pathRef.value ?? pathRef), page.props))
}

/**
 * Prefix an internal path with the active microsite's path segment. Sits
 * OUTSIDE the language prefix (`/derby/fr/about-us`), mirroring ParsePath which
 * strips the microsite segment first. Pure helper, same pass-through rules as
 * withLangPrefix; a no-op when prefix is empty (i.e. on the root site).
 */
export function withMicrositePrefix(path, prefix) {
  if (typeof path !== 'string' || path.length === 0) {
    return path
  }
  if (!prefix) {
    return path
  }
  if (!path.startsWith('/') || path.startsWith('//')) {
    return path
  }

  const micrositePrefix = `/${prefix}`
  if (path === micrositePrefix || path.startsWith(`${micrositePrefix}/`)) {
    return path
  }

  return path === '/' ? micrositePrefix : `${micrositePrefix}${path}`
}

/**
 * Inverse of withMicrositePrefix — remove the leading microsite segment so a
 * site-relative path can be matched/parsed (e.g. the property-search PATH_REGEX
 * which expects `/sales/...`, not `/derby/sales/...`). No-op on the root site.
 */
export function stripMicrositePrefix(path, prefix) {
  if (typeof path !== 'string' || !prefix) {
    return path
  }

  const micrositePrefix = `/${prefix}`
  if (path === micrositePrefix) {
    return '/'
  }
  if (path.startsWith(`${micrositePrefix}/`)) {
    return path.slice(micrositePrefix.length)
  }

  return path
}

/**
 * Apply both the language and microsite prefixes (microsite outermost), reading
 * both from the Inertia page props. The single transform every internal link
 * should pass through.
 */
export function withSitePrefix(path, props) {
  return withMicrositePrefix(withLangPrefix(path, props.lang, props.website?.default_language), props.website?.path_prefix)
}
